/* eslint-disable @typescript-eslint/no-explicit-any */
import { type Virtualizer } from "@tanstack/virtual-core";
import { type RefObject } from "react";

export const makeObserveWindowOffset =
  <TElement extends HTMLElement>(listContainerRef: RefObject<TElement>) =>
  (instance: Virtualizer<Window, any>, cb: (offset: number) => void) => {
    const element = instance.scrollElement;
    if (!element) {
      return;
    }

    const handler = () => {
      const scroll = element[instance.options.horizontal ? "scrollX" : "scrollY"];
      const elementOffset = listContainerRef.current
        ? documentOffset(listContainerRef.current)[instance.options.horizontal ? "left" : "top"]
        : 0;
      const adjustedScroll = Math.max(0, scroll - elementOffset);
      cb(adjustedScroll);
    };
    handler();

    element.addEventListener("scroll", handler, {
      passive: true,
    });

    return () => {
      element.removeEventListener("scroll", handler);
    };
  };

export const makeWindowScrollOffset = <TElement extends HTMLElement>(listContainerRef: RefObject<TElement>) => {
  let ranOnce = false;
  return <T extends Window>(
    offset: number,
    { adjustments = 0, behavior }: { adjustments?: number; behavior?: ScrollBehavior },
    instance: Virtualizer<T, any>,
  ) => {
    if (!ranOnce) {
      // This is needed so that on page load we don't scroll immediately to the list
      ranOnce = true;
      return;
    }
    const elementOffset = listContainerRef.current
      ? documentOffset(listContainerRef.current)[instance.options.horizontal ? "left" : "top"]
      : 0;

    const toOffset = offset + adjustments + elementOffset;

    instance.scrollElement?.scrollTo?.({
      [instance.options.horizontal ? "left" : "top"]: toOffset,
      behavior,
    });
  };
};

export const makeInitialWindowOffset =
  <TElement extends HTMLElement>(listContainerRef: RefObject<TElement>) =>
  () => {
    const elementOffset = listContainerRef.current ? documentOffset(listContainerRef.current).top : 0;
    return Math.max(0, window.scrollY - elementOffset);
  };

function documentOffset(element: HTMLElement) {
  const { left, top } = element.getBoundingClientRect();
  return { top: top + window.scrollY, left: left + window.scrollX };
}
